"use client";

import React, { useState, useEffect } from "react";
import { Phone, Mail, Facebook, Twitter, Linkedin, Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="201626" className="flex gap-4">
        <div className="h-8 w-20 bg-muted animate-pulse rounded-sm" />
        <div className="h-8 w-20 bg-muted animate-pulse rounded-sm" />
        <div className="h-8 w-20 bg-muted animate-pulse rounded-sm" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative px-4 py-2 text-[11px] font-default uppercase tracking-widest text-foreground hover:text-primary transition-colors group">
                {item.label}
                <span className="absolute bottom-0 left-4 right-4 h-[1px] bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-8">
        <div className="h-10 w-full bg-muted animate-pulse rounded-sm" />
        <div className="h-10 w-full bg-muted animate-pulse rounded-sm" />
        <div className="h-10 w-full bg-muted animate-pulse rounded-sm" />
      </div>;
  }
  return <div className="flex flex-col gap-2 mt-8">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} className="block py-4 text-sm font-default uppercase tracking-widest text-foreground hover:text-primary transition-colors border-b border-border/10">
          {item.label}
        </Link>)}
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className="dark sticky top-0 z-50 w-full flex flex-col bg-background text-foreground">
      {}
      <div className="h-[40px] bg-background border-b border-border/10 hidden md:flex items-center justify-between px-8 text-[11px] font-default uppercase tracking-widest text-muted-foreground">
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-2">
            <Phone className="size-3 text-primary" />
            <span>Private Client Access</span>
          </div>
          <div className="flex items-center gap-2">
            <Mail className="size-3 text-primary" />
            <span>inquiry@1066.vc</span>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <Link to="#" className="hover:text-primary transition-colors duration-300">
            <Facebook className="size-3" />
            <span className="sr-only">Facebook</span>
          </Link>
          <Link to="#" className="hover:text-primary transition-colors duration-300">
            <Twitter className="size-3" />
            <span className="sr-only">Twitter</span>
          </Link>
          <Link to="#" className="hover:text-primary transition-colors duration-300">
            <Linkedin className="size-3" />
            <span className="sr-only">LinkedIn</span>
          </Link>
        </div>
      </div>

      {}
      <div className={`w-full transition-all duration-500 ease-out ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-2xl" : "bg-background"}`}>
        <div className="container mx-auto px-8 md:px-12 h-24 flex items-center justify-between">
          <div className="flex-shrink-0">
            <WvcLogo className="h-7 w-auto" />
          </div>

          <div className="hidden md:flex items-center justify-end flex-1">
            <MenuProvider menu_id="45">
              <DesktopMenu />
            </MenuProvider>
          </div>

          <div className="md:hidden flex items-center">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-foreground hover:bg-white/5">
                  <Menu className="size-6" />
                  <span className="sr-only">Toggle mobile menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[350px] bg-background border-l border-border/20">
                <SheetTitle className="sr-only">1066 Ventures Navigation</SheetTitle>
                <div className="flex items-center justify-start mb-12 mt-6">
                  <WvcLogo className="h-6 w-auto" />
                </div>
                <MenuProvider menu_id="45">
                  <MobileMenu />
                </MenuProvider>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}